import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
  viewChild,
} from '@angular/core';
import type {
  AnimationConfigWithPath,
  AnimationItem,
  BMCompleteEvent,
  BMCompleteLoopEvent,
  BMDestroyEvent,
  LottiePlayer,
} from 'lottie-web';
import player from 'lottie-web';
import { PlatformService } from '../../../services/platform.service';
import { NgStyle } from '@angular/common';

@Component({
  selector: 'app-lottie',
  templateUrl: './lottie.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle],
})
export class LottieComponent implements AfterViewInit, OnDestroy {
  private _platform = inject(PlatformService);

  /**
   * Config properties
   */
  readonly path = input<string>(undefined);
  readonly assetsPath = input<string>(undefined);
  readonly loop = input(true);
  readonly autoplay = input(true);
  readonly animationSpeed = input<number>(0.5);
  readonly width = input<string>('100%');
  readonly name = input<string | any>(undefined);

  /**
   * Output events
   */
  public readonly complete$ = output<BMCompleteEvent>();
  public readonly loopComplete$ = output<BMCompleteLoopEvent>();
  public readonly destroy$ = output<BMDestroyEvent>();
  public readonly dataReady$ = output<boolean>();

  /**
   * Lottie default container
   */
  readonly lottieContainer = viewChild<ElementRef>('lottieContainer');

  /**
   * Native intersection observer
   * @private
   */
  private _intersectionObserver: IntersectionObserver;

  /**
   * Animation item
   * @private
   */
  private _animation: AnimationItem;

  private _destroyed = false;

  private _onComplete = (event: BMCompleteEvent) => {
    if (this._destroyed || !this._platform.isBrowser) return;
    this.complete$.emit(event);
  };
  private _onLoopComplete = (event: BMCompleteLoopEvent) => {
    if (this._destroyed || !this._platform.isBrowser) return;
    this.loopComplete$.emit(event);
  };
  private _onLottieDestroy = (event: BMDestroyEvent) => {
    if (this._destroyed || !this._platform.isBrowser) return;
    this.destroy$.emit(event);
  };
  private _onDataReady = () => {
    if (this._destroyed || !this._platform.isBrowser) return;
    this.dataReady$.emit(true);
  };

  /**
   * Access to player SSR
   */
  get player(): LottiePlayer {
    if (this._platform.isBrowser) {
      return player;
    }
  }

  public playAnimation() {
    this._animation?.goToAndPlay(0, true);
  }


  ngAfterViewInit() {
    if (this._platform.isBrowser) {
      this._initializeIntersectionObserver();
      this._observeVisibility();
    }
  }

  ngOnDestroy() {
    if (!this._platform.isBrowser) {
      return;
    }
    this._destroyed = true;
    this._intersectionObserver?.disconnect();
    this._intersectionObserver = undefined;

    // Remove Lottie listeners first, then destroy animation
    if (this._animation) {
      this._animation.removeEventListener('complete', this._onComplete);
      this._animation.removeEventListener('loopComplete', this._onLoopComplete);
      this._animation.removeEventListener('destroy', this._onLottieDestroy);
      this._animation.removeEventListener('data_ready', this._onDataReady);
      this._animation.destroy();
      this._animation = undefined;
    }
  }

  /**
   * Init observer and set entries
   * If entries exists load animation and render svg
   * @private
   */
  private _initializeIntersectionObserver(): void {
    this._intersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        if (this._destroyed) return;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            this._loadAnimation();
            const el = this.lottieContainer()?.nativeElement;
            if (el) this._intersectionObserver?.unobserve(el);
            break;
          }
        }
      },
      {threshold: 0.5},
    );
  }

  /**
   * Observe visibility of element
   * @private
   */
  private _observeVisibility(): void {
    const lottieContainer = this.lottieContainer();
    if (this._platform.isBrowser && lottieContainer.nativeElement) {
      this._intersectionObserver.observe(lottieContainer.nativeElement);
    }
  }

  /**
   * Load animation by path
   * @private
   */
  private async _loadAnimation(): Promise<void> {
    if (this._destroyed || !this._platform.isBrowser) return;

    if (!this.player) return;

    const el = this.lottieContainer()?.nativeElement ?? null;
    if (!el) return;

    const opts: AnimationConfigWithPath = {
      container: el,
      path: this.path(),
      assetsPath: this.assetsPath(),
      renderer: 'svg',
      loop: this.loop(),
      autoplay: this.autoplay(),
      ...(this.name() ? {name: this.name()} : {}),
    };

    this._animation = this.player.loadAnimation(opts);
    this.player.setSpeed(this.animationSpeed());
    this._dispatchEvents();
  }

  private _dispatchEvents() {
    this._animation.addEventListener('complete', this._onComplete);
    this._animation.addEventListener('loopComplete', this._onLoopComplete);
    this._animation.addEventListener('destroy', this._onLottieDestroy);
    this._animation.addEventListener('data_ready', this._onDataReady);
  }
}
